import { Checkbox, Flex, Form, Input, theme, Typography } from 'antd';
import { type ReactNode } from 'react';
import OrderItemsList, { type ItemOption } from './order-items-list';
import { type WizardItemValue, type WizardValues } from './site-menu-order-wizard.mapper';
import { OverflowComponent } from '../../../../../../components/overflow.component';
import { type ScheduledMealOptionsDto } from '../../../../../../dto/order-options/site-order-options-dto';

const NothingToAdd = ({ children }: { children: ReactNode }): ReactNode => (
  <Flex justify="center" style={{ minHeight: 300 }}>
    <Typography.Title level={3} style={{ alignContent: 'center' }}>
      {children}
    </Typography.Title>
  </Flex>
);

interface BeveragesStepProps {
  meal: ScheduledMealOptionsDto | undefined;
  selectedCount: number;
  allowedCount: number;
  showPrepackCheckbox: boolean;
}

/** The beverages ordered with the meal, up to the number its quantity allows */
export const BeveragesStep = ({
  meal,
  selectedCount,
  allowedCount,
  showPrepackCheckbox,
}: BeveragesStepProps): ReactNode => {
  const { token } = theme.useToken();
  if (!meal?.beverages.length) {
    return <NothingToAdd>You can&apos;t add beverages for {meal?.mealCategory.name}</NothingToAdd>;
  }
  return (
    <Flex vertical gap="small" style={{ padding: token.paddingMD }}>
      <Typography.Text style={{ textAlign: 'left' }}>
        You have selected <b>{selectedCount}</b> beverage(s) so far. (You are allowed up to{' '}
        <b>{allowedCount}</b> beverage(s))
      </Typography.Text>
      <OrderItemsList
        name="beverages"
        itemKey="beverage"
        items={meal.beverages}
        placeholder="Please select beverages"
        missingItemMessage="Missing Beverage"
        addLabel="Add Beverage"
      />
      {showPrepackCheckbox && (
        <Form.Item
          wrapperCol={{ span: 24 }}
          labelCol={{ span: 0 }}
          valuePropName="checked"
          name="prepackBeverages"
        >
          <Checkbox style={{ width: '100%' }}>Prepack beverage with meal components.</Checkbox>
        </Form.Item>
      )}
      {selectedCount < allowedCount && (
        <Typography.Text type="warning" style={{ fontSize: token.fontSizeLG }}>
          You have chosen less beverages than the number of meals.
        </Typography.Text>
      )}
      {selectedCount > allowedCount && (
        <Typography.Text type="danger" style={{ fontSize: token.fontSizeLG }}>
          you can&apos;t select beverages more than the total number of meals.
        </Typography.Text>
      )}
    </Flex>
  );
};

interface ItemsStepProps {
  name: 'extraBeverages' | 'supplies';
  itemKey: 'beverage' | 'supply';
  items: ItemOption[];
  nothingToAddMessage: ReactNode;
  placeholder: string;
  missingItemMessage: string;
  addLabel: string;
  showDescription?: boolean;
}

/** The extra beverages or the supplies ordered with the meal */
export const ItemsStep = ({
  items,
  nothingToAddMessage,
  ...listProps
}: ItemsStepProps): ReactNode =>
  items.length > 0 ? (
    <Flex vertical style={{ padding: 20, minHeight: 300 }}>
      <OrderItemsList items={items} {...listProps} />
    </Flex>
  ) : (
    <NothingToAdd>{nothingToAddMessage}</NothingToAdd>
  );

const OrderedItems = ({
  title,
  items,
  options,
  itemKey,
}: {
  title: string;
  items: WizardItemValue[];
  options: ItemOption[];
  itemKey: 'beverage' | 'supply';
}): ReactNode => (
  <Flex vertical>
    <Flex>
      <Typography.Title level={3}>{title}</Typography.Title>
    </Flex>
    <OverflowComponent
      layout="vertical"
      maxCount={5}
      elementsList={items.map((item, index) => (
        <Typography.Text key={index}>
          {options.find(({ id }) => String(id) === item?.[itemKey]?.id)?.name}{' '}
          <b>x{item.quantity}</b>
        </Typography.Text>
      ))}
    />
  </Flex>
);

/** Last step: what is ordered, and a comment about it */
export const ConfirmStep = ({ meal }: { meal: ScheduledMealOptionsDto | undefined }): ReactNode => {
  const form = Form.useFormInstance<WizardValues>();
  const values: WizardValues = form.getFieldsValue(true);
  const allBeverages = [...(meal?.beverages ?? []), ...(meal?.extraBeverages ?? [])];
  return (
    <Flex vertical style={{ padding: 20 }}>
      <Flex>
        <Typography.Title level={2}>Meal summary</Typography.Title>
      </Flex>
      <Flex gap="small">
        <Typography.Text>You have chosen {values.quantity} meals of</Typography.Text>
        <Typography.Text strong>{meal?.mealCategory.name}</Typography.Text>
      </Flex>
      <Flex gap="large" justify="space-between" style={{ width: '70%' }}>
        <OrderedItems
          title="Beverages"
          items={values.beverages ?? []}
          options={allBeverages}
          itemKey="beverage"
        />
        <OrderedItems
          title="Extra beverages"
          items={values.extraBeverages ?? []}
          options={allBeverages}
          itemKey="beverage"
        />
        <OrderedItems
          title="Supplies"
          items={values.supplies ?? []}
          options={meal?.supplies ?? []}
          itemKey="supply"
        />
      </Flex>
      <Form.Item label="Additional Comment" name="comment">
        <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} />
      </Form.Item>
    </Flex>
  );
};
